/**
 * utilitiesFootball3D.js — Three.js 3D Object Factory for KickViz
 * ════════════════════════════════════════════════════════════════════
 *
 * PURPOSE
 *   Provides all factory functions that build the 3D objects rendered in the
 *   Viewport3D component: the football pitch, players (GLB character meshes
 *   with textures + animations), ball, cones, targets, transition arrows,
 *   zones, look-at markers, and manual path editors.
 *
 * ROLE IN THE APP
 *   This module is the single source of truth for 3D geometry creation.
 *   Viewport3D.js imports these functions to populate the scene on every
 *   state change (pitch state, team data, edit mode transitions).  No other
 *   module constructs Three.js meshes directly.
 *
 * KEY SYSTEMS
 *   • Texture generators      — procedural grass, ball, number, name, shadow
 *     (no external image assets required; used as fallback when GLBs absent)
 *   • Pitch creation          — GLB-first with procedural line/mesh fallback
 *   • Character asset cache   — _charCache holds the base mesh, skin/hair/
 *     boots/team/number textures, and pose animation clips; preloaded once
 *     and synchronously cloned per-player via SkeletonUtils.clone
 *   • Player object creation  — clones the cached base mesh, resolves team
 *     textures (outfield vs GK, custom vs default), attaches hair to the
 *     Head bone, and applies a distance-based pose animation
 *   • Pose system             — pose_t (T-pose) is applied first so hair can
 *     be attached at the correct bone world position; then switched to
 *     pose_stand / pose_walk / pose_jog / pose_run based on move distance
 *   • Transition arrows       — V2 graphic-track path arrows (drawn in
 *     Viewport3D via PolylineCurve + tube geometry, not here)
 *
 * RELATIONSHIPS
 *   • Consumed by Viewport3D.js (the main 3D viewport React component)
 *   • GLB models loaded via assets3d/manifest.js (isAssetEnabled, loadModel)
 *   • Character textures fetched from backend /uploads/viz-assets/
 *
 * EXPORTED FUNCTIONS
 *   createFootballPitch, createPlayerObject, createBallObject, createConeObject,
 *   createTargetObject,
 *   createZoneObject, createZoneEditPoints,
 *   getPlayerObjectName, preloadPlayerModel, preloadBallModel,
 *   preloadCharacterAssets, preloadTeamTextures, preloadNumberTextures,
 *   collectNeededStyles
 */

import * as THREE from 'three';
import { isAssetEnabled, loadModel } from './assets3d/manifest.js';

// ═══════════════════════════════════════════════════════════════
//  SKELETON UTILITIES — lazy-loaded for proper skinned mesh cloning
// ═══════════════════════════════════════════════════════════════

/**
 * Lazy-load SkeletonUtils.clone from three/examples.
 * SkeletonUtils.clone is required for proper skinned-mesh duplication —
 * a regular Object3D.clone(true) does not correctly duplicate bone/skeleton
 * references, so animated characters would break without this.
 * @returns {Promise<Function>} the SkeletonUtils.clone function
 */
let _skeletonCloneFn = null;
async function _ensureSkeletonClone() {
    if (!_skeletonCloneFn) {
        const mod = await import('three/examples/jsm/utils/SkeletonUtils.js');
        _skeletonCloneFn = mod.clone;
    }
    return _skeletonCloneFn;
}

/**
 * Synchronous wrapper around SkeletonUtils.clone.
 * If SkeletonUtils hasn't been preloaded yet (via _ensureSkeletonClone),
 * falls back to regular Object3D.clone(true) — the skeleton may not animate
 * correctly in that case, but the mesh will still render.
 * @param {THREE.Object3D} source - the object to clone
 * @returns {THREE.Object3D} a deep clone with properly duplicated skeleton
 */
function _skeletonClone(source) {
    if (_skeletonCloneFn) return _skeletonCloneFn(source);
    // Fallback — regular clone (skeleton may not animate correctly)
    return source.clone(true);
}

// ═══════════════════════════════════════════════════════════════
//  TEXTURE GENERATORS
//  Procedural canvas-based textures — no external image assets needed.
//  Used as fallbacks when GLB models are unavailable, and for the
//  procedural pitch, ball, numbers, names, and shadow blobs.
// ═══════════════════════════════════════════════════════════════

/**
 * Generate a high-detail procedural grass texture for the pitch surface.
 * Combines a radial gradient base, mowing stripes with blade direction,
 * ~18 000 individual blade strokes, wet-sheen patches, wear patterns at
 * goals and centre circle, and fine per-pixel noise.
 * @param {number} length - pitch length in metres (used for aspect reference)
 * @param {number} width - pitch width in metres (used for aspect reference)
 * @returns {THREE.CanvasTexture} a 2048×2048 tiling grass texture
 */
function createGrassTexture(length, width) {
    const canvas = document.createElement('canvas');
    canvas.width = 2048;
    canvas.height = 2048;
    const ctx = canvas.getContext('2d');

    // ── Base grass gradient (cooler at edges, warmer at center) ──
    const baseGrad = ctx.createRadialGradient(1024, 1024, 200, 1024, 1024, 1400);
    baseGrad.addColorStop(0, '#3a8a4e');
    baseGrad.addColorStop(0.6, '#2d7a3e');
    baseGrad.addColorStop(1, '#246532');
    ctx.fillStyle = baseGrad;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // ── Mowing stripes (alternating darker/lighter bands with blade direction) ──
    const stripeCount = 22;
    const stripeWidth = canvas.width / stripeCount;
    for (let i = 0; i < stripeCount; i++) {
        const isLight = i % 2 === 0;
        ctx.fillStyle = isLight ? 'rgba(55, 140, 78, 0.35)' : 'rgba(30, 80, 45, 0.35)';
        ctx.fillRect(i * stripeWidth, 0, stripeWidth, canvas.height);

        // Blade direction overlay — subtle horizontal lines within each stripe
        ctx.strokeStyle = isLight ? 'rgba(60, 150, 85, 0.08)' : 'rgba(35, 90, 50, 0.08)';
        ctx.lineWidth = 1;
        for (let y = 0; y < canvas.height; y += 3) {
            ctx.beginPath();
            ctx.moveTo(i * stripeWidth, y);
            ctx.lineTo((i + 1) * stripeWidth, y);
            ctx.stroke();
        }
    }

    // ── Individual grass blade strokes (adds micro-detail) ──
    for (let i = 0; i < 18000; i++) {
        const x = Math.random() * canvas.width;
        const y = Math.random() * canvas.height;
        const len = 2 + Math.random() * 4;
        const angle = Math.random() * Math.PI * 2;
        const hue = 95 + Math.random() * 30;
        const sat = 30 + Math.random() * 30;
        const light = 20 + Math.random() * 25;
        ctx.strokeStyle = `hsla(${hue}, ${sat}%, ${light}%, ${0.15 + Math.random() * 0.2})`;
        ctx.lineWidth = 0.8;
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.lineTo(x + Math.cos(angle) * len, y + Math.sin(angle) * len);
        ctx.stroke();
    }

    // ── Wet sheen patches (subtle glossy areas) ──
    for (let i = 0; i < 6; i++) {
        const cx = Math.random() * canvas.width;
        const cy = Math.random() * canvas.height;
        const r = 80 + Math.random() * 120;
        const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
        grad.addColorStop(0, 'rgba(120, 180, 130, 0.08)');
        grad.addColorStop(0.5, 'rgba(120, 180, 130, 0.03)');
        grad.addColorStop(1, 'rgba(120, 180, 130, 0)');
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(cx, cy, r, 0, Math.PI * 2);
        ctx.fill();
    }

    // ── Wear patterns (darker patches from heavy use) ──
    const goalWear = (cx, cy) => {
        const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, 200);
        grad.addColorStop(0, 'rgba(50, 75, 38, 0.5)');
        grad.addColorStop(0.5, 'rgba(50, 75, 38, 0.22)');
        grad.addColorStop(1, 'rgba(50, 75, 38, 0)');
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.ellipse(cx, cy, 200, 140, 0, 0, Math.PI * 2);
        ctx.fill();
    };
    goalWear(canvas.width * 0.5, canvas.height * 0.04);
    goalWear(canvas.width * 0.5, canvas.height * 0.96);

    // Center circle wear
    const centerGrad = ctx.createRadialGradient(1024, 1024, 0, 1024, 1024, 140);
    centerGrad.addColorStop(0, 'rgba(45, 72, 35, 0.3)');
    centerGrad.addColorStop(0.6, 'rgba(45, 72, 35, 0.12)');
    centerGrad.addColorStop(1, 'rgba(45, 72, 35, 0)');
    ctx.fillStyle = centerGrad;
    ctx.beginPath();
    ctx.arc(1024, 1024, 140, 0, Math.PI * 2);
    ctx.fill();

    // ── Fine noise ──
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;
    for (let i = 0; i < data.length; i += 4) {
        const noise = (Math.random() - 0.5) * 14;
        data[i] = Math.max(0, Math.min(255, data[i] + noise));
        data[i + 1] = Math.max(0, Math.min(255, data[i + 1] + noise));
        data[i + 2] = Math.max(0, Math.min(255, data[i + 2] + noise));
    }
    ctx.putImageData(imageData, 0, 0);

    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.repeat.set(2, 2);
    texture.anisotropy = 16;
    texture.colorSpace = THREE.SRGBColorSpace;
    return texture;
}

/**
 * Generate a procedural normal map for the grass surface.
 * Draws ~4 000 short strokes representing blade bumps, encoding X/Y normals
 * in the red/green channels (blue = 255 for up-facing).
 * @returns {THREE.CanvasTexture} a 512×512 tiling normal map
 */
function createGrassNormalMap() {
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 512;
    const ctx = canvas.getContext('2d');

    ctx.fillStyle = 'rgb(128, 128, 255)';
    ctx.fillRect(0, 0, 512, 512);

    for (let i = 0; i < 4000; i++) {
        const x = Math.random() * 512;
        const y = Math.random() * 512;
        const len = 2 + Math.random() * 3;
        const angle = Math.random() * Math.PI * 2;
        const dx = Math.cos(angle) * len;
        const dy = Math.sin(angle) * len;
        // Simulate blade bump: green channel = Y normal, red = X normal
        const nx = Math.max(0, Math.min(255, 128 + dx * 8));
        const ny = Math.max(0, Math.min(255, 128 - dy * 8));
        ctx.strokeStyle = `rgb(${nx}, ${ny}, 255)`;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(x, y);
        ctx.lineTo(x + dx, y + dy);
        ctx.stroke();
    }

    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.repeat.set(8, 8);
    return texture;
}

/**
 * Generate a procedural soccer ball texture with a truncated-icosahedron
 * pattern (black pentagons + subtle hexagon seams on a white base).
 * @returns {THREE.CanvasTexture} a 1024×512 tiling ball texture
 */
function createSoccerBallTexture() {
    const canvas = document.createElement('canvas');
    canvas.width = 1024;
    canvas.height = 512;
    const ctx = canvas.getContext('2d');

    // White base with subtle off-white gradient
    const grad = ctx.createLinearGradient(0, 0, 0, 512);
    grad.addColorStop(0, '#f8f8f8');
    grad.addColorStop(0.5, '#ffffff');
    grad.addColorStop(1, '#f0f0f0');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, 1024, 512);

    // Draw black pentagons in a truncated icosahedron pattern
    ctx.fillStyle = '#1a1a1a';
    const cols = 10;
    const rows = 5;
    const cellW = 1024 / cols;
    const cellH = 512 / rows;

    for (let row = 0; row < rows; row++) {
        for (let col = 0; col < cols; col++) {
            const x = col * cellW + cellW / 2 + (row % 2 === 0 ? 0 : cellW / 2);
            const y = row * cellH + cellH / 2;
            if (x > 1024) continue;

            if ((row + col) % 3 === 0) {
                drawPentagon(ctx, x, y, cellW * 0.32);
            } else {
                // Subtle hexagon seams
                ctx.strokeStyle = 'rgba(180, 180, 180, 0.35)';
                ctx.lineWidth = 1.5;
                drawHexagon(ctx, x, y, cellW * 0.38);
                ctx.stroke();
            }
        }
    }

    // Subtle shading
    const shadeGrad = ctx.createRadialGradient(512, 256, 100, 512, 256, 500);
    shadeGrad.addColorStop(0, 'rgba(0,0,0,0)');
    shadeGrad.addColorStop(1, 'rgba(0,0,0,0.06)');
    ctx.fillStyle = shadeGrad;
    ctx.fillRect(0, 0, 1024, 512);

    const texture = new THREE.CanvasTexture(canvas);
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.anisotropy = 16;
    texture.colorSpace = THREE.SRGBColorSpace;
    return texture;
}

/**
 * Draw a filled regular pentagon on a 2D canvas context.
 * @param {CanvasRenderingContext2D} ctx - canvas 2D context
 * @param {number} cx - centre X
 * @param {number} cy - centre Y
 * @param {number} radius - circumradius
 */
function drawPentagon(ctx, cx, cy, radius) {
    ctx.beginPath();
    for (let i = 0; i < 5; i++) {
        const angle = (i / 5) * Math.PI * 2 - Math.PI / 2;
        const x = cx + Math.cos(angle) * radius;
        const y = cy + Math.sin(angle) * radius;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
    }
    ctx.closePath();
    ctx.fill();
}

/**
 * Trace (without filling) a regular hexagon path on a 2D canvas context.
 * Call ctx.stroke() after to render the outline.
 * @param {CanvasRenderingContext2D} ctx - canvas 2D context
 * @param {number} cx - centre X
 * @param {number} cy - centre Y
 * @param {number} radius - circumradius
 */
function drawHexagon(ctx, cx, cy, radius) {
    ctx.beginPath();
    for (let i = 0; i < 6; i++) {
        const angle = (i / 6) * Math.PI * 2;
        const x = cx + Math.cos(angle) * radius;
        const y = cy + Math.sin(angle) * radius;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
    }
    ctx.closePath();
}

/**
 * Create a canvas with a player number rendered in the given colour.
 * Optionally draws a circular background with a ring outline.
 * @param {string|number} text - the number to render
 * @param {string} [color='#ffffff'] - text colour
 * @param {string|null} [bg=null] - background fill colour, or null for transparent
 * @returns {HTMLCanvasElement} a 256×256 canvas
 */
function createNumberCanvas(text, color = '#ffffff', bg = null) {
    const canvas = document.createElement('canvas');
    canvas.width = 256;
    canvas.height = 256;
    const ctx = canvas.getContext('2d');

    if (bg) {
        ctx.fillStyle = bg;
        ctx.beginPath();
        ctx.arc(128, 128, 104, 0, Math.PI * 2);
        ctx.fill();
        // Ring outline
        ctx.strokeStyle = 'rgba(255,255,255,0.3)';
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.arc(128, 128, 104, 0, Math.PI * 2);
        ctx.stroke();
    }

    ctx.fillStyle = color;
    ctx.font = 'bold 140px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(String(text), 128, 134);

    return canvas;
}

/**
 * Create a soft shadow blob — a flat radial-gradient circle mesh placed
 * under objects to simulate a grounded shadow without expensive shadow maps.
 * The mesh is rotated flat (X = -π/2), positioned slightly above y=0 to
 * avoid z-fighting, and flagged with userData.isShadow so raycasting and
 * disposal can skip it.
 * @param {number} [radius=0.5] - radius of the shadow circle
 * @returns {THREE.Mesh} a flat shadow blob mesh
 */
function createShadowBlob(radius = 0.5) {
    const canvas = document.createElement('canvas');
    canvas.width = 128;
    canvas.height = 128;
    const ctx = canvas.getContext('2d');
    const grad = ctx.createRadialGradient(64, 64, 0, 64, 64, 64);
    grad.addColorStop(0, 'rgba(0,0,0,0.45)');
    grad.addColorStop(0.5, 'rgba(0,0,0,0.22)');
    grad.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, 128, 128);

    const tex = new THREE.CanvasTexture(canvas);
    const geo = new THREE.CircleGeometry(radius, 32);
    const mat = new THREE.MeshBasicMaterial({
        map: tex,
        transparent: true,
        opacity: 0.5,
        depthWrite: false,
    });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.rotation.x = -Math.PI / 2;
    mesh.position.y = 0.008;
    mesh.userData.isShadow = true;
    return mesh;
}


// ═══════════════════════════════════════════════════════════════
//  PITCH
//
//  ASSET: Pitch Model — pitch_style_a.glb in src/assets3d/models/pitch/
//  Includes: pitch surface (named 'pitch_surface'), fences, goals, floodlights
//  ASSET: Pitch Texture — swap by placing grass.jpg in src/assets3d/textures/pitch/
//  See src/assets3d/README.md for instructions
// ═══════════════════════════════════════════════════════════════

/**
 * Create the 3D football pitch.
 *
 * Tries to load the GLB pitch model first (via assets3d/manifest). If the GLB
 * is available, it is centred at the origin with its surface at y=0 and the
 * 'pitch_surface' mesh is flagged for raycasting (userData.isPlacementSurface).
 *
 * If no GLB is available, falls back to a fully procedural pitch: grass-textured
 * plane, surrounding dark area, painted lines (boundary, halfway, centre circle,
 * penalty boxes, spots, arcs, corner arcs), corner flags, and detailed goals
 * with net structures.
 *
 * @param {object} [opts] - pitch dimension options (all in metres)
 * @param {number} [opts.pitchLength=105] - total pitch length
 * @param {number} [opts.pitchWidth=68] - total pitch width
 * @param {number} [opts.goalWidth=7.32] - goal opening width
 * @param {number} [opts.goalHeight=2.44] - goal crossbar height
 * @param {number} [opts.goalPostRadius=0.08] - goal post radius
 * @param {number} [opts.lineThickness=0.12] - painted line thickness
 * @param {number} [opts.boxWidth=40.32] - penalty box width
 * @param {number} [opts.boxLength=16.5] - penalty box depth
 * @param {number} [opts.smallBoxWidth=18.32] - six-yard box width
 * @param {number} [opts.smallBoxLength=5.5] - six-yard box depth
 * @param {number} [opts.centerCircleRadius=9.15] - centre circle radius
 * @param {number} [opts.penaltyArcRadius=9.15] - penalty arc radius
 * @param {number} [opts.penaltySpotOffset=11] - penalty spot distance from goal
 * @param {number} [opts.curveSegments=64] - tessellation for curved lines
 * @returns {Promise<THREE.Group>} a group containing the complete pitch
 */
export async function createFootballPitch({
    pitchLength = 105,
    pitchWidth = 68,
    goalWidth = 7.32,
    goalHeight = 2.44,
    goalPostRadius = 0.08,
    lineThickness = 0.12,
    boxWidth = 40.32,
    boxLength = 16.5,
    smallBoxWidth = 18.32,
    smallBoxLength = 5.5,
    centerCircleRadius = 9.15,
    penaltyArcRadius = 9.15,
    penaltySpotOffset = 11,
    curveSegments = 64,
} = {}) {
    const group = new THREE.Group();
    group.name = 'FootballPitch';

    const halfLength = pitchLength * 0.5;
    const halfWidth = pitchWidth * 0.5;
    const halfGoalWidth = goalWidth * 0.5;
    const lineY = 0.02;

    // ── Try loading GLB pitch model ──
    if (isAssetEnabled('pitch')) {
        const model = await loadModel('pitch');
        if (model) {
            // GLB is already Y-up and correctly scaled (internal node scale 0.01 = cm→m).
            // Do NOT apply any rotation or additional scale.
            model.updateMatrixWorld(true);

            // Ensure shadows on all meshes
            model.traverse((child) => {
                if (child.isMesh) {
                    child.castShadow = true;
                    child.receiveShadow = true;
                }
            });

            // Flag pitch_surface for raycasting, center model at origin on y=0
            const pitchSurface = model.getObjectByName('pitch_surface');
            if (pitchSurface) {
                pitchSurface.userData.isPlacementSurface = true;
                pitchSurface.receiveShadow = true;

                // Center pitch_surface at origin, sitting on y=0
                const box = new THREE.Box3().setFromObject(pitchSurface);
                const center = new THREE.Vector3();
                box.getCenter(center);
                model.position.x -= center.x;
                model.position.z -= center.z;
                model.position.y -= box.min.y;
            }

            group.add(model);
            return group;
        }
    }

    // ── Procedural fallback (original code below) ──

    // ── Grass textured pitch ──
    const grassTex = createGrassTexture(pitchLength, pitchWidth);
    const grassNormal = createGrassNormalMap();
    const pitchMaterial = new THREE.MeshStandardMaterial({
        map: grassTex,
        normalMap: grassNormal,
        normalScale: new THREE.Vector2(0.35, 0.35),
        roughness: 0.75,
        metalness: 0.02,
    });

    const pitchMesh = new THREE.Mesh(
        new THREE.PlaneGeometry(pitchLength, pitchWidth),
        pitchMaterial
    );
    pitchMesh.rotation.x = -Math.PI / 2;
    pitchMesh.receiveShadow = true;
    pitchMesh.name = 'pitch_surface';
    pitchMesh.userData.isPlacementSurface = true;
    group.add(pitchMesh);

    // ── Surrounding dark area (running track / surrounding ground) ──
    const surroundMat = new THREE.MeshStandardMaterial({
        color: 0x1a2818,
        roughness: 0.9,
        metalness: 0.0,
    });
    const surround = new THREE.Mesh(
        new THREE.PlaneGeometry(pitchLength + 30, pitchWidth + 30),
        surroundMat
    );
    surround.rotation.x = -Math.PI / 2;
    surround.position.y = -0.1;
    surround.receiveShadow = true;
    group.add(surround);

    // ── Outer ring (subtle transition border) ──
    const borderMat = new THREE.MeshStandardMaterial({
        color: 0x2d4a2d,
        roughness: 0.85,
    });
    const border = new THREE.Mesh(
        new THREE.RingGeometry(
            Math.max(pitchLength, pitchWidth) * 0.5 + 1,
            Math.max(pitchLength, pitchWidth) * 0.5 + 2,
            64
        ),
        borderMat
    );
    border.rotation.x = -Math.PI / 2;
    border.position.y = -0.05;
    group.add(border);

    // ── Line materials (painted-on look: white, slightly emissive, matte) ──
    const lineMaterial = new THREE.MeshStandardMaterial({
        color: 0xffffff,
        roughness: 0.6,
        metalness: 0.0,
        emissive: 0xffffff,
        emissiveIntensity: 0.08,
        side: THREE.DoubleSide,
    });
    const lineBasicMaterial = new THREE.LineBasicMaterial({
        color: 0xffffff,
        linewidth: 2,
    });

    // ── Line helpers (procedural fallback) ──

    /** Create a horizontal pitch line (along X axis) at the given length/thickness. */
    function createHorizontalLine(length, thickness) {
        const mesh = new THREE.Mesh(new THREE.PlaneGeometry(length, thickness), lineMaterial);
        mesh.rotation.x = -Math.PI / 2;
        mesh.position.y = lineY;
        return mesh;
    }

    /** Create a vertical pitch line (along Z axis) at the given length/thickness. */
    function createVerticalLine(length, thickness) {
        const mesh = new THREE.Mesh(new THREE.PlaneGeometry(thickness, length), lineMaterial);
        mesh.rotation.x = -Math.PI / 2;
        mesh.position.y = lineY;
        return mesh;
    }

    /** Create a full circle line at the origin using line segments. */
    function createCircleLine(radius, segments = 64) {
        const points = [];
        for (let i = 0; i <= segments; i++) {
            const a = (i / segments) * Math.PI * 2;
            points.push(new THREE.Vector3(Math.cos(a) * radius, lineY, Math.sin(a) * radius));
        }
        const geometry = new THREE.BufferGeometry().setFromPoints(points);
        return new THREE.Line(geometry, lineBasicMaterial);
    }

    /** Create a partial arc line from startAngle to endAngle at the origin. */
    function createArcLine(radius, startAngle, endAngle, segments = 64) {
        const points = [];
        for (let i = 0; i <= segments; i++) {
            const t = i / segments;
            const a = startAngle + (endAngle - startAngle) * t;
            points.push(new THREE.Vector3(Math.cos(a) * radius, lineY, Math.sin(a) * radius));
        }
        const geometry = new THREE.BufferGeometry().setFromPoints(points);
        return new THREE.Line(geometry, lineBasicMaterial);
    }

    /** Create a quarter-circle corner arc at the given corner position. */
    function createCornerArc(radius = 1, cx = 0, cz = 0, startAngle = 0) {
        const points = [];
        for (let i = 0; i <= 16; i++) {
            const a = startAngle + (i / 16) * (Math.PI / 2);
            points.push(new THREE.Vector3(cx + Math.cos(a) * radius, lineY, cz + Math.sin(a) * radius));
        }
        const geometry = new THREE.BufferGeometry().setFromPoints(points);
        return new THREE.Line(geometry, lineBasicMaterial);
    }

    /** Create a small filled circle spot (penalty spot, centre spot). */
    function createSpot(radius = 0.25) {
        const mesh = new THREE.Mesh(new THREE.CircleGeometry(radius, 24), lineMaterial);
        mesh.rotation.x = -Math.PI / 2;
        mesh.position.y = lineY;
        return mesh;
    }

    /**
     * Create the three lines forming a penalty or six-yard box.
     * @param {number} edgeX - X position of the goal line
     * @param {number} depth - box depth from the goal line
     * @param {number} width - box width
     * @param {number} sideSign - +1 for left goal, -1 for right goal
     * @returns {THREE.Mesh[]} [frontLine, topLine, bottomLine]
     */
    function createBoxLines(edgeX, depth, width, sideSign) {
        const halfW = width * 0.5;
        const frontX = edgeX + sideSign * depth;
        const midX = (edgeX + frontX) * 0.5;
        const front = createVerticalLine(width, lineThickness);
        front.position.x = frontX;
        const top = createHorizontalLine(depth, lineThickness);
        top.position.x = midX; top.position.z = -halfW;
        const bottom = createHorizontalLine(depth, lineThickness);
        bottom.position.x = midX; bottom.position.z = halfW;
        return [front, top, bottom];
    }

    /**
     * Create a detailed goal at the given X position: posts, crossbar, post caps,
     * net panels (back, top, sides with wireframe material), and support struts.
     * The net extends behind the goal line in the direction away from centre.
     * @param {number} xPos - X coordinate of the goal line
     * @returns {THREE.Group} the complete goal group
     */
    function createGoalAtX(xPos) {
        const goalGroup = new THREE.Group();

        const postMat = new THREE.MeshStandardMaterial({
            color: 0xffffff,
            roughness: 0.25,
            metalness: 0.5,
            emissive: 0xffffff,
            emissiveIntensity: 0.05,
        });

        // Net material — semi-transparent with grid pattern
        const netMat = new THREE.MeshStandardMaterial({
            color: 0xffffff,
            transparent: true,
            opacity: 0.2,
            side: THREE.DoubleSide,
            wireframe: true,
        });
        const netFineMat = new THREE.MeshStandardMaterial({
            color: 0xeeeeee,
            transparent: true,
            opacity: 0.12,
            side: THREE.DoubleSide,
            wireframe: true,
        });

        // Posts (tapered, thicker at base)
        const postGeo = new THREE.CylinderGeometry(goalPostRadius * 0.85, goalPostRadius, goalHeight, 16);
        const leftPost = new THREE.Mesh(postGeo, postMat);
        leftPost.position.set(xPos, goalHeight * 0.5, -halfGoalWidth);
        leftPost.castShadow = true;
        const rightPost = new THREE.Mesh(postGeo, postMat);
        rightPost.position.set(xPos, goalHeight * 0.5, halfGoalWidth);
        rightPost.castShadow = true;

        // Crossbar
        const crossbarGeo = new THREE.CylinderGeometry(goalPostRadius, goalPostRadius, goalWidth, 16);
        const crossbar = new THREE.Mesh(crossbarGeo, postMat);
        crossbar.rotation.x = Math.PI * 0.5;
        crossbar.position.set(xPos, goalHeight, 0);
        crossbar.castShadow = true;

        // Post caps (small spheres on top)
        const capGeo = new THREE.SphereGeometry(goalPostRadius * 1.2, 12, 8);
        const capL = new THREE.Mesh(capGeo, postMat);
        capL.position.set(xPos, goalHeight, -halfGoalWidth);
        const capR = new THREE.Mesh(capGeo, postMat);
        capR.position.set(xPos, goalHeight, halfGoalWidth);
        capL.castShadow = true; capR.castShadow = true;

        goalGroup.add(leftPost, rightPost, crossbar, capL, capR);

        // ── Net structure ──
        const netDepth = 1.8;
        const netDir = xPos < 0 ? -1 : 1;

        // Back panel (fine grid)
        const netBack = new THREE.Mesh(
            new THREE.PlaneGeometry(goalWidth, goalHeight, 24, 8),
            netFineMat
        );
        netBack.rotation.y = Math.PI / 2;
        netBack.position.set(xPos + netDir * netDepth, goalHeight * 0.5, 0);

        // Top panel
        const netTop = new THREE.Mesh(
            new THREE.PlaneGeometry(netDepth, goalWidth, 6, 24),
            netFineMat
        );
        netTop.rotation.x = -Math.PI / 2;
        netTop.rotation.z = Math.PI / 2;
        netTop.position.set(xPos + netDir * netDepth * 0.5, goalHeight, 0);

        // Side panels
        const netSideGeo = new THREE.PlaneGeometry(netDepth, goalHeight, 6, 8);
        const netSideL = new THREE.Mesh(netSideGeo, netMat);
        netSideL.position.set(xPos + netDir * netDepth * 0.5, goalHeight * 0.5, -halfGoalWidth);
        const netSideR = new THREE.Mesh(netSideGeo, netMat);
        netSideR.position.set(xPos + netDir * netDepth * 0.5, goalHeight * 0.5, halfGoalWidth);

        // Support struts (diagonal from post to back-top)
        const strutMat = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.3, metalness: 0.4 });
        const strutGeo = new THREE.CylinderGeometry(0.03, 0.03, 1, 6);
        for (const zPos of [-halfGoalWidth, halfGoalWidth]) {
            const strut = new THREE.Mesh(strutGeo, strutMat);
            const from = new THREE.Vector3(xPos, goalHeight, zPos);
            const to = new THREE.Vector3(xPos + netDir * netDepth, goalHeight, zPos);
            const mid = new THREE.Vector3().lerpVectors(from, to, 0.5);
            strut.position.copy(mid);
            strut.lookAt(to);
            strut.rotateX(Math.PI / 2);
            strut.scale.y = from.distanceTo(to);
            goalGroup.add(strut);
        }

        goalGroup.add(netBack, netTop, netSideL, netSideR);

        return goalGroup;
    }

    // ── Outer boundary ──
    group.add(createHorizontalLine(pitchLength, lineThickness).translateZ(-halfWidth));
    group.add(createHorizontalLine(pitchLength, lineThickness).translateZ(halfWidth));
    group.add(createVerticalLine(pitchWidth, lineThickness).translateX(-halfLength));
    group.add(createVerticalLine(pitchWidth, lineThickness).translateX(halfLength));

    // ── Halfway line ──
    group.add(createVerticalLine(pitchWidth, lineThickness));

    // ── Centre circle + spot ──
    group.add(createCircleLine(centerCircleRadius, curveSegments));
    group.add(createSpot(0.25));

    // ── Big boxes ──
    group.add(...createBoxLines(-halfLength, boxLength, boxWidth, +1));
    group.add(...createBoxLines(halfLength, boxLength, boxWidth, -1));

    // ── Small boxes ──
    group.add(...createBoxLines(-halfLength, smallBoxLength, smallBoxWidth, +1));
    group.add(...createBoxLines(halfLength, smallBoxLength, smallBoxWidth, -1));

    // ── Penalty spots ──
    const leftPS = createSpot(0.08);
    leftPS.position.set(-halfLength + penaltySpotOffset, lineY, 0);
    group.add(leftPS);
    const rightPS = createSpot(0.08);
    rightPS.position.set(halfLength - penaltySpotOffset, lineY, 0);
    group.add(rightPS);

    // ── Penalty arcs ──
    const leftArc = createArcLine(penaltyArcRadius, -Math.PI / 2, Math.PI / 2, curveSegments);
    leftArc.position.set(-halfLength + penaltySpotOffset, 0, 0);
    group.add(leftArc);
    const rightArc = createArcLine(penaltyArcRadius, Math.PI / 2, Math.PI * 1.5, curveSegments);
    rightArc.position.set(halfLength - penaltySpotOffset, 0, 0);
    group.add(rightArc);

    // ── Corner arcs ──
    group.add(createCornerArc(1, -halfLength, -halfWidth, 0));
    group.add(createCornerArc(1, halfLength, -halfWidth, Math.PI / 2));
    group.add(createCornerArc(1, halfLength, halfWidth, Math.PI));
    group.add(createCornerArc(1, -halfLength, halfWidth, -Math.PI / 2));

    // ── Corner flags (animated, subtle sway) ──
    const flagPoleMat = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.4, metalness: 0.2 });
    const flagMat = new THREE.MeshStandardMaterial({ color: 0xffcc00, roughness: 0.4, emissive: 0xffcc00, emissiveIntensity: 0.15, side: THREE.DoubleSide });

    /**
     * Create a corner flag (pole + animated flag mesh).
     * The flag mesh is tagged with userData.isCornerFlag and userData.baseAngle
     * so the animate loop can apply a subtle sway rotation.
     * @param {number} cx - corner X position
     * @param {number} cz - corner Z position
     * @param {number} angle - base sway phase offset
     * @returns {THREE.Group} pole + flag group
     */
    function createCornerFlag(cx, cz, angle) {
        const flagGroup = new THREE.Group();
        const pole = new THREE.Mesh(
            new THREE.CylinderGeometry(0.03, 0.03, 1.5, 8),
            flagPoleMat
        );
        pole.position.set(cx, 0.75, cz);
        pole.castShadow = true;
        flagGroup.add(pole);

        const flag = new THREE.Mesh(
            new THREE.PlaneGeometry(0.4, 0.25),
            flagMat
        );
        flag.position.set(cx + 0.2, 1.3, cz);
        flag.userData.isCornerFlag = true;
        flag.userData.baseAngle = angle;
        flagGroup.add(flag);

        return flagGroup;
    }

    group.add(createCornerFlag(-halfLength, -halfWidth, 0));
    group.add(createCornerFlag(halfLength, -halfWidth, Math.PI / 2));
    group.add(createCornerFlag(halfLength, halfWidth, Math.PI));
    group.add(createCornerFlag(-halfLength, halfWidth, -Math.PI / 2));

    // ── Goals ──
    group.add(createGoalAtX(-halfLength));
    group.add(createGoalAtX(halfLength));

    return group;
}


// ═══════════════════════════════════════════════════════════════
//  PLAYER — CHARACTER ASSET SYSTEM
// ═══════════════════════════════════════════════════════════════
//  Loads base_mesh.glb + hair GLBs + textures from the backend server's
//  /uploads/viz-assets/ directory.  All assets are preloaded once and
//  cached for synchronous cloning per-player via SkeletonUtils.clone.
//
//  Cache structure (_charCache):
//    baseMesh            — THREE.Object3D, the auto-fitted base character
//    baseMeshYOffset     — Y offset so feet sit at y=0
//    animations          — THREE.AnimationClip[] from base_mesh.glb
//                          (pose_t, pose_stand, pose_walk, pose_jog, pose_run)
//    hairMeshes          — { style_000: THREE.Group } cloned hair GLBs
//    hairTextures        — { style_000: THREE.Texture }
//    skinTextures        — { style_000: THREE.Texture }
//    bootsTexture        — THREE.Texture
//    defaultTeamTextures — { kv_home, kv_away } each with shirtFront/Back,
//                          gkShirtFront/Back, shortsFront/Back, socksFront
//    customTeamTextures  — { url: THREE.Texture } lazy-loaded by URL
//    numberTextures      — { '01'–'99': THREE.Texture } lazy-loaded
// ═══════════════════════════════════════════════════════════════

/**
 * Global character asset cache.
 * Populated by preloadCharacterAssets(); read synchronously by createPlayerObject().
 * @type {object}
 */
const _charCache = {
    baseMesh: null,
    baseMeshScale: 1,
    baseMeshYOffset: 0,     // Y offset applied to base mesh (feet at y=0)
    animations: null,       // Array<THREE.AnimationClip> from base_mesh.glb
    hairMeshes: {},    // style_000 → THREE.Group (cloned hair.glb)
    hairNormalTextures: {},
    hairNormalFiles: {},
    hairTextures: {},  // style_000 → THREE.Texture
    skinTextures: {},  // style_000 → THREE.Texture
    bootsTexture: null,
    defaultTeamTextures: {},  // kv_home/kv_away/kv_gk → { shirtFront, shirtBack, shortsFront, shortsBack }
    customTeamTextures: {},   // url → THREE.Texture (lazy-loaded, cached by URL)
    numberTextures: {},       // '01'-'99' → THREE.Texture (lazy-loaded, cached)
};

// ═══════════════════════════════════════════════════════════════
//  POSE SELECTION — speed-driven animation
// ═══════════════════════════════════════════════════════════════

/**
 * Per-frame animation update for a moving player (used during timeline
 * playback). Picks the clip by the player's CURRENT SPEED (m/s) and scales
 * the playback rate so the stride cadence matches the speed:
 *   < 0.15 m/s → pose_stand · < 1.6 → pose_walk · < 3.8 → pose_jog · else pose_run
 * Overlapping bands give hysteresis so the pose doesn't flicker around a
 * threshold; the rate is updated continuously while the pose stays the same.
 * @param {THREE.Object3D} playerObj - wrapper returned by createPlayerObject
 * @param {number} speed - metres per second (from per-frame displacement)
 * @param {number} blendSeconds - short for scrubbing so a wheel step is visible
 */
export function updatePlayerAnimation(playerObj, speed, direction = 1, blendSeconds = 0.3) {
    if (!playerObj?.userData?.mixer) return;
    const mixer = playerObj.userData.mixer;
    const clips = _charCache.animations || [];
    if (clips.length === 0) return;

    // Band table (m/s) with overlaps = hysteresis.
    const BANDS = {
        pose_stand: [0, 0.25],
        pose_walk: [0.12, 1.8],
        pose_jog: [1.2, 4.2],
        pose_run: [3.2, Infinity],
    };
    // Natural speed (m/s) each clip is authored for — used to scale the
    // playback rate so the stride cadence matches the player's actual speed.
    const POSE_NATURAL = { pose_stand: 1, pose_walk: 1.4, pose_jog: 3.0, pose_run: 6.0 };
    const cur = playerObj.userData.currentPose || 'pose_stand';
    let poseName;
    const [lo, hi] = BANDS[cur] || BANDS.pose_stand;
    if (speed >= lo && speed <= hi) {
        poseName = cur; // still inside the current band — no switch
    } else {
        poseName = speed < 0.15 ? 'pose_stand'
            : speed < 1.6 ? 'pose_walk'
            : speed < 3.8 ? 'pose_jog'
            : 'pose_run';
    }
    if (speed > 0.000001 && poseName === 'pose_stand') poseName = 'pose_walk';
    if (speed <= 0.000001) {
        poseName = 'pose_stand';
        // Stop residual gait actions immediately; an idle player must not keep
        // stepping while a run-to-idle crossfade or an old branch decays.
        for (const clip of clips) {
            if (['pose_walk', 'pose_jog', 'pose_run'].includes(clip.name)) mixer.existingAction(clip)?.stop();
        }
    }
    // Continuous rate within the pose: scale toward the clip's natural speed.
    const natural = POSE_NATURAL[poseName] ?? 1;
    const rate = poseName === 'pose_stand' ? 1 : Math.min(2.5, Math.max(0.1, speed / natural));

    if (playerObj.userData.currentPose !== poseName) {
        const clip = clips.find(c => c.name === poseName);
        if (!clip) return;
        if (playerObj.userData.currentAction) {
            playerObj.userData.currentAction.fadeOut(blendSeconds);
        }
        const newAction = mixer.clipAction(clip);
        newAction.setLoop(THREE.LoopRepeat, Infinity); // loops continuously
        newAction.reset().setEffectiveWeight(1).fadeIn(poseName === 'pose_stand' ? 0 : blendSeconds).play();
        // Continue from the OLD clip's cycle phase (stride continuity) instead
        // of a random restart — pose changes no longer jump the run cycle.
        if (clip.duration > 0) {
            const oldClip = playerObj.userData.currentAction?.getClip?.();
            const oldDur = oldClip?.duration || 0;
            const oldTime = playerObj.userData.currentAction?.time || 0;
            const phase = oldDur > 0 ? (((oldTime % oldDur) / oldDur) + 1) % 1 : 0;
            newAction.time = phase * clip.duration;
        } else {
            newAction.time = 0;
        }
        playerObj.userData.currentAction = newAction;
        playerObj.userData.currentPose = poseName;
    }
    if (playerObj.userData.currentAction) {
        playerObj.userData.currentAction.setEffectiveTimeScale(poseName === 'pose_stand' ? rate : rate * (direction < 0 ? -1 : 1));
    }
}

/**
 * True once the base character mesh (base_mesh.glb) has been cached.
 * Player objects fall back to a procedural body when this is false, so callers
 * that build players during asset preloading can re-build once it flips true.
 * @returns {boolean}
 */
export function isCharacterMeshReady() {
    return !!(_charCache && _charCache.baseMesh);
}

/**
 * Preload character assets from the backend server.
 * Only loads styles that are actually needed by the selected teams.
 * Idempotent — safe to call again when teams change; cached assets are skipped.
 *
 * @param {string} backendUrl - e.g. '' (relative) or 'https://api.example.com'
 * @param {object} options
 * @param {string[]} options.neededSkinStyles - e.g. ['style_000', 'style_003']
 * @param {string[]} options.neededHairStyles - e.g. ['style_000', 'style_001']
 */
export async function preloadCharacterAssets(backendUrl, options = {}) {
    const { neededSkinStyles = ['style_000'], neededHairStyles = ['style_000'] } = options;
    const vizBase = `${backendUrl}/uploads/viz-assets`;
    const texLoader = new THREE.TextureLoader();

    // Pre-load SkeletonUtils for proper skinned mesh cloning
    await _ensureSkeletonClone();

    // ── Load base mesh (always needed, once) ──
    if (!_charCache.baseMesh) {
        try {
            const { GLTFLoader } = await import('three/examples/jsm/loaders/GLTFLoader.js');
            const loader = new GLTFLoader();
            const gltf = await loader.loadAsync(`${vizBase}/base_mesh.glb?v=avatar-v14`);
            const model = gltf.scene;

            // Store animation clips (pose_stand, pose_walk, pose_jog, pose_run, etc.)
            _charCache.animations = gltf.animations || [];
            console.log(`[character] base_mesh.glb loaded (${_charCache.animations.length} animations: ${_charCache.animations.map(a => a.name).join(', ')})`);

            // Auto-fit: scale to ~1.8m height
            const box = new THREE.Box3().setFromObject(model);
            const size = new THREE.Vector3();
            box.getSize(size);
            if (size.y > 0) {
                const fitScale = 1.8 / size.y;
                _charCache.baseMeshScale = fitScale;
                model.scale.multiplyScalar(fitScale);
            }
            // Offset so feet are at y=0
            const fittedBox = new THREE.Box3().setFromObject(model);
            model.position.y -= fittedBox.min.y;
            _charCache.baseMeshYOffset = -fittedBox.min.y;

            _charCache.baseMesh = model;
        } catch (e) {
            console.error('[character] CRITICAL: Failed to load base_mesh.glb:', e);
            throw new Error('Failed to load player model (base_mesh.glb). Check backend connection.');
        }
    }

    // ── Load ONLY needed hair styles ──
    for (const hs of neededHairStyles) {
        if (!_charCache.hairMeshes[hs]) {
            try {
                const { GLTFLoader } = await import('three/examples/jsm/loaders/GLTFLoader.js');
                const loader = new GLTFLoader();
                const gltf = await loader.loadAsync(`${vizBase}/hair/${hs}/hair.glb?v=avatar-v19-chunks`);
                const hairModel = gltf.scene;
                hairModel.traverse(o => {
                    if (o.userData.hairNormalMap === 'hair_normal.png') {
                        _charCache.hairNormalFiles[hs] = 'hair_normal.png';
                    }
                });
                // Hair shares the source character's coordinates. Apply the
                // identical fit transform before attaching it to the Head bone.
                hairModel.scale.multiplyScalar(_charCache.baseMeshScale);
                hairModel.position.y += _charCache.baseMeshYOffset;
                // Keep the hair's natural export position — it will be parented to Head bone later

                _charCache.hairMeshes[hs] = hairModel;
                console.log(`[character] hair ${hs} loaded`);
            } catch (e) {
                console.error(`[character] Failed to load hair ${hs}:`, e);
            }
        }
        if (!_charCache.hairTextures[hs]) {
            try {
                const hTex = await texLoader.loadAsync(`${vizBase}/hair/${hs}/hair_texture.png?v=avatar-v19-chunks`);
                _charCache.hairTextures[hs] = configureTextureForGLB(hTex);
            } catch (e) {
                console.error(`[character] Failed to load hair texture ${hs}:`, e);
            }
        }
        // Optional surface detail is declared by the mesh, preserving legacy assets.
        if (_charCache.hairNormalFiles[hs] && !_charCache.hairNormalTextures[hs]) {
            try {
                const normal = await texLoader.loadAsync(`${vizBase}/hair/${hs}/hair_normal.png?v=avatar-v19-chunks`);
                normal.flipY = false;
                normal.colorSpace = THREE.NoColorSpace;
                _charCache.hairNormalTextures[hs] = normal;
            } catch (e) {
                console.warn(`[character] Hair surface detail unavailable for ${hs}:`, e);
            }
        }
    }

    // ── Load ONLY needed skin styles ──
    for (const ss of neededSkinStyles) {
        if (!_charCache.skinTextures[ss]) {
            try {
                const tex = await texLoader.loadAsync(`${vizBase}/skin/${ss}.png?v=avatar-v24-clean-skin`);
                _charCache.skinTextures[ss] = configureTextureForGLB(tex);
            } catch (e) {
                console.error(`[character] Failed to load skin ${ss}:`, e);
            }
        }
    }

    // ── Load boots texture (always needed, once) ──
    if (!_charCache.bootsTexture) {
        try {
            const tex = await texLoader.loadAsync(`${vizBase}/boots/style_000.png`);
            _charCache.bootsTexture = configureTextureForGLB(tex);
        } catch (e) {
            console.error('[character] Failed to load boots texture:', e);
        }
    }

    // ── Load default team textures (kv_home, kv_away — each includes outfield + GK + socks) ──
    for (const teamKey of ['kv_home', 'kv_away']) {
        if (!_charCache.defaultTeamTextures[teamKey]) {
            try {
                const [shirtFront, gkShirtFront, shortsFront, gkShortsFront, socksFront,
                       shirtBack, gkShirtBack, shortsBack, gkShortsBack, gkSocksFront] = await Promise.all([
                    texLoader.loadAsync(`${vizBase}/teams/${teamKey}/shirt_front.png`),
                    texLoader.loadAsync(`${vizBase}/teams/${teamKey}/shirt_front_gk.png`),
                    texLoader.loadAsync(`${vizBase}/teams/${teamKey}/shorts_front.png`),
                    texLoader.loadAsync(`${vizBase}/teams/${teamKey}/shorts_front_gk.png`),
                    texLoader.loadAsync(`${vizBase}/teams/${teamKey}/socks_front.png`).catch(() => null),
                    texLoader.loadAsync(`${vizBase}/teams/${teamKey}/shirt_back.png`).catch(() => null),
                    texLoader.loadAsync(`${vizBase}/teams/${teamKey}/shirt_back_gk.png`).catch(() => null),
                    texLoader.loadAsync(`${vizBase}/teams/${teamKey}/shorts_back.png`).catch(() => null),
                    texLoader.loadAsync(`${vizBase}/teams/${teamKey}/shorts_back_gk.png`).catch(() => null),
                    texLoader.loadAsync(`${vizBase}/teams/${teamKey}/socks_front_gk.png`).catch(() => null),
                ]);
                _charCache.defaultTeamTextures[teamKey] = {
                    shirtFront: configureTextureForGLB(shirtFront),
                    gkShirtFront: configureTextureForGLB(gkShirtFront),
                    shortsFront: configureTextureForGLB(shortsFront),
                    gkShortsFront: configureTextureForGLB(gkShortsFront),
                    socksFront: socksFront ? configureTextureForGLB(socksFront) : null,
                    shirtBack: shirtBack ? configureTextureForGLB(shirtBack) : null,
                    gkShirtBack: gkShirtBack ? configureTextureForGLB(gkShirtBack) : null,
                    shortsBack: shortsBack ? configureTextureForGLB(shortsBack) : null,
                    gkShortsBack: gkShortsBack ? configureTextureForGLB(gkShortsBack) : null,
                    gkSocksFront: gkSocksFront ? configureTextureForGLB(gkSocksFront) : null,
                };
            } catch (e) {
                console.error(`[character] Failed to load default team textures for ${teamKey}:`, e);
            }
        }
    }

    console.log('[character] Assets preloaded:', {
        skin: Object.keys(_charCache.skinTextures),
        hair: Object.keys(_charCache.hairMeshes),
    });
}

/**
 * Collect unique skin/hair styles from team player data.
 * @param {...object} teams - team objects with playerInfo arrays
 * @returns {{ skinStyles: string[], hairStyles: string[] }}
 */
export function collectNeededStyles(...teams) {
    const skin = new Set(['style_000']);  // always include default fallback
    const hair = new Set(['style_000']);
    const numbers = new Set();
    for (const team of teams) {
        if (!team?.playerInfo) continue;
        for (const p of team.playerInfo) {
            if (p.skinStyle) skin.add(p.skinStyle);
            if (p.hairStyle) hair.add(p.hairStyle);
            const num = String(p.number || '').trim().replace(/^0+/, '') || '0';
            if (parseInt(num) >= 1 && parseInt(num) <= 99) numbers.add(num.padStart(2, '0'));
        }
    }
    return {
        skinStyles: [...skin],
        hairStyles: [...hair],
        playerNumbers: [...numbers],
    };
}

/**
 * Preload number textures for all players in the given teams.
 * @param {string} backendUrl - base backend URL
 * @param  {...object} teams - team objects with playerInfo arrays
 */
export async function preloadNumberTextures(backendUrl, ...teams) {
    const { playerNumbers } = collectNeededStyles(...teams);
    await Promise.all(playerNumbers.map(n => loadNumberTexture(backendUrl, n)));
}

/**
 * Load a custom team texture by URL (cached in _charCache.customTeamTextures).
 * @param {string} url - full URL to the texture image
 * @returns {Promise<THREE.Texture>} the configured texture
 * @throws {Error} if the texture cannot be loaded — caller must handle
 */
async function loadCustomTexture(url) {
    if (!url) return null;
    if (_charCache.customTeamTextures[url]) return _charCache.customTeamTextures[url];
    const loader = new THREE.TextureLoader();
    const tex = await loader.loadAsync(url);
    _charCache.customTeamTextures[url] = configureTextureForGLB(tex);
    return _charCache.customTeamTextures[url];
}

/**
 * Configure a texture for use on GLB meshes (UV-correct).
 * GLB models export UVs with origin at bottom-left, but three.js TextureLoader
 * flips Y by default (origin top-left). Setting flipY=false matches the GLB UVs.
 * Also sets wrapping and anisotropy for crisp rendering.
 */
function configureTextureForGLB(texture) {
    if (!texture) return null;
    texture.flipY = false;       // GLB UVs expect non-flipped Y
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.anisotropy = 16;     // sharp at grazing angles
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.needsUpdate = true;
    return texture;
}
/**
 * Resolve team textures for a player, choosing between custom and default textures.
 *
 * For GK players: uses the home/away team's GK textures (gkShirtFront, etc.)
 * rather than a separate kv_gk folder.  Custom URLs (from teamTextures) take
 * priority if the texture is already cached; otherwise falls back to defaults.
 *
 * @param {object|null} teamTextures - custom texture URLs (may be null)
 * @param {boolean} isGK - whether this player is a goalkeeper
 * @param {string} side - 'home' or 'away' (determines default texture set)
 * @returns {{ shirtFront, shirtBack, shortsFront, shortsBack, socksFront }} resolved textures
 */
function resolveTeamTextures(teamTextures, isGK, side) {
    const defaultKey = side === 'home' ? 'kv_home' : 'kv_away';
    const defaults = _charCache.defaultTeamTextures[defaultKey] || _charCache.defaultTeamTextures['kv_home'] || {};

    if (!teamTextures) {
        if (isGK) {
            return {
                shirtFront: defaults.gkShirtFront || defaults.shirtFront,
                shirtBack: defaults.gkShirtBack || defaults.shirtBack || defaults.gkShirtFront || defaults.shirtFront,
                shortsFront: defaults.gkShortsFront || defaults.shortsFront,
                shortsBack: defaults.gkShortsBack || defaults.shortsBack || defaults.gkShortsFront || defaults.shortsFront,
                socksFront: defaults.gkSocksFront || defaults.socksFront || null,
            };
        }
        return {
            shirtFront: defaults.shirtFront,
            shirtBack: defaults.shirtBack || defaults.shirtFront,
            shortsFront: defaults.shortsFront,
            shortsBack: defaults.shortsBack || defaults.shortsFront,
            socksFront: defaults.socksFront || null,
        };
    }

    const resolve = (customUrl, defaultTex) => {
        if (customUrl && _charCache.customTeamTextures[customUrl]) return _charCache.customTeamTextures[customUrl];
        return defaultTex;
    };

    if (isGK) {
        return {
            shirtFront: resolve(teamTextures.gkShirtFront, defaults.gkShirtFront || defaults.shirtFront),
            shirtBack: resolve(teamTextures.gkShirtBack, defaults.gkShirtBack || defaults.shirtBack || defaults.gkShirtFront || defaults.shirtFront),
            shortsFront: resolve(teamTextures.gkShortsFront, defaults.gkShortsFront || defaults.shortsFront),
            shortsBack: resolve(teamTextures.gkShortsBack, defaults.gkShortsBack || defaults.shortsBack || defaults.gkShortsFront || defaults.shortsFront),
            socksFront: resolve(teamTextures.gkSocksFront, defaults.gkSocksFront || defaults.socksFront || null),
        };
    }
    return {
        shirtFront: resolve(teamTextures.shirtFront, defaults.shirtFront),
        shirtBack: resolve(teamTextures.shirtBack, defaults.shirtBack || defaults.shirtFront),
        shortsFront: resolve(teamTextures.shortsFront, defaults.shortsFront),
        shortsBack: resolve(teamTextures.shortsBack, defaults.shortsBack || defaults.shortsFront),
        socksFront: resolve(teamTextures.socksFront, defaults.socksFront || null),
    };
}

/**
 * Load a number texture by player number (cached).
 * Files are named 01.png through 99.png in viz-assets/numbers/.
 * @param {string} backendUrl - base backend URL
 * @param {string} playerNumber - e.g. '1', '10', '99'
 * @returns {THREE.Texture|null}
 */
async function loadNumberTexture(backendUrl, playerNumber) {
    const num = String(playerNumber || '').trim().replace(/^0+/, '') || '0';
    const padded = num.padStart(2, '0');
    if (padded === '00' || parseInt(num) < 1 || parseInt(num) > 99) return null;

    if (_charCache.numberTextures[padded]) return _charCache.numberTextures[padded];
    try {
        const vizBase = `${backendUrl}/uploads/viz-assets`;
        const loader = new THREE.TextureLoader();
        const tex = await loader.loadAsync(`${vizBase}/numbers/${padded}.png`);
        _charCache.numberTextures[padded] = configureTextureForGLB(tex);
        return _charCache.numberTextures[padded];
    } catch (e) {
        console.error(`[character] Failed to load number texture ${padded}:`, e);
        return null;
    }
}

/**
 * Preload all custom team textures for a team.
  * Throws on failure so the caller can surface the error — does NOT silently
  * fall back to default textures.
  * @param {object} teamTextures - { shirtFront, shirtBack, ... } URL strings
  */
 export async function preloadTeamTextures(teamTextures) {
     if (!teamTextures) return;
     const entries = [
         ['shirtFront', teamTextures.shirtFront],
         ['shirtBack', teamTextures.shirtBack],
         ['shortsFront', teamTextures.shortsFront],
         ['shortsBack', teamTextures.shortsBack],
         ['gkShirtFront', teamTextures.gkShirtFront],
         ['gkShirtBack', teamTextures.gkShirtBack],
         ['gkShortsFront', teamTextures.gkShortsFront],
         ['gkShortsBack', teamTextures.gkShortsBack],
         ['socksFront', teamTextures.socksFront],
         ['gkSocksFront', teamTextures.gkSocksFront],
     ].filter(([, url]) => Boolean(url));

     const results = await Promise.allSettled(entries.map(([, url]) => loadCustomTexture(url)));
     const failures = results.filter(r => r.status === 'rejected');
     if (failures.length > 0) {
         const failedUrls = entries.filter((_, i) => results[i].status === 'rejected').map(([k]) => k);
         console.error('[character] Failed to load custom team textures:', failedUrls, failures.map(f => f.reason));
         throw new Error(`Failed to load team textures: ${failedUrls.join(', ')}`);
     }
 }

// ═══════════════════════════════════════════════════════════════
//  ASSET: Player Model — loaded from backend viz-assets/base_mesh.glb
//  Textures and hair applied per-player based on team/player settings.
// ═══════════════════════════════════════════════════════════════

// Module-level cache: pre-load GLB once, clone synchronously on each use.
let _playerModelCache = null;

/**
 * Preload the legacy GLB player model (fallback path).
 * Uses assets3d/manifest to load the GLB once, auto-fits to ~1.8m height,
 * and offsets so feet are at y=0.  The cached model is cloned synchronously
 * by createPlayerObject when the character asset system is unavailable.
 * @returns {Promise<THREE.Object3D|null>} the cached player model, or null
 */
export async function preloadPlayerModel() {
    if (_playerModelCache) return _playerModelCache;
    if (!isAssetEnabled('players')) return null;
    try {
        const model = await loadModel('players');
        if (model) {
            // Auto-fit: scale to ~1.8m height
            const box = new THREE.Box3().setFromObject(model);
            const size = new THREE.Vector3();
            box.getSize(size);
            const height = size.y;
            if (height > 0) {
                const fitScale = 1.8 / height;
                model.scale.multiplyScalar(fitScale);
            }
            // Offset so feet are at y=0
            const fittedBox = new THREE.Box3().setFromObject(model);
            model.position.y -= fittedBox.min.y;
            _playerModelCache = model;
        }
    } catch (e) {
        console.error('[player] preload failed, using procedural', e);
    }
    return _playerModelCache;
}

// ══════════════════════════════════════════════════════════════════════
// PLAYER NAME LABELS
// ══════════════════════════════════════════════════════════════════════

/** Label height above the player's feet, per body build (world units = metres). */
export const PLAYER_LABEL_Y = {
    character: 2.15,   // character mesh is ~1.84m tall (measured from base_mesh.glb)
    procedural: 1.45,  // primitives-only fallback is ~1.1m tall
};

/** On-screen height (canvas px) of a label's text, used by the viewport sizing. */
export const PLAYER_LABEL_FONT_PX = 64;
/** Canvas height of the label texture — the text occupies the middle half. */
export const PLAYER_LABEL_CANVAS_H = 128;

/**
 * Create a camera-facing name label for a player.
 *
 * A THREE.Sprite always faces the camera (billboarding is built into the sprite
 * shader), so the label stays readable from any orbit angle. The text is drawn
 * into a 2D canvas, once per player, so it stays crisp: white fill with a dark
 * outline that holds up over grass, the sky and white pitch lines.
 *
 * The sprite renders on top of the scene (depthTest false): a name tag is UI,
 * and it must not be clipped by the player's own body or by a goal. Sizing is
 * done per frame by the viewport so the label keeps a constant on-screen size
 * at any zoom — the scale set here is only a placeholder for the first frame.
 *
 * @param {string} text - label text (player name)
 * @returns {THREE.Sprite} sprite flagged with userData.isPlayerLabel = true
 */
export function createPlayerLabel(text) {
    const label = String(text ?? '').trim();
    const PAD = 28; // canvas padding (px) so the outline is never clipped
    const FONT = `bold ${PLAYER_LABEL_FONT_PX}px system-ui, -apple-system, "Segoe UI", Roboto, sans-serif`;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx.font = FONT;
    canvas.width = Math.max(PLAYER_LABEL_CANVAS_H, Math.ceil(ctx.measureText(label).width) + PAD * 2);
    canvas.height = PLAYER_LABEL_CANVAS_H;

    // Setting width/height resets the 2D context state — configure it after.
    ctx.font = FONT;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.lineJoin = 'round';
    ctx.lineWidth = 10;
    ctx.strokeStyle = 'rgba(0,0,0,0.85)'; // outline first, so the fill sits on top
    ctx.strokeText(label, canvas.width / 2, canvas.height / 2);
    ctx.fillStyle = '#ffffff';
    ctx.fillText(label, canvas.width / 2, canvas.height / 2);

    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.anisotropy = 4;

    const sprite = new THREE.Sprite(new THREE.SpriteMaterial({
        map: texture,
        transparent: true,
        depthTest: false,  // draw over the scene — a tag, not geometry
        depthWrite: false,
        toneMapped: false, // keep the white pure white
    }));
    sprite.name = 'PlayerLabel';
    sprite.userData.isPlayerLabel = true;
    sprite.userData.pickable = false;
    sprite.raycast = () => {}; // Names are display-only, never selection geometry.
    sprite.userData.labelText = label;
    // Width/height ratio of the texture, so the viewport can scale both axes.
    sprite.userData.labelAspect = canvas.width / canvas.height;
    sprite.renderOrder = 999; // last, so tags sit over everything
    // Placeholder size; the viewport re-scales this every frame for a constant
    // on-screen size. 0.5m tall ≈ a readable tag at a tactical camera distance.
    sprite.scale.set(0.5 * sprite.userData.labelAspect, 0.5, 1);
    return sprite;
}

/**
 * Attach a name label to a player container (wrapper group).
 *
 * Uses the player's name, falling back to "#number" for unnamed players, and
 * sits the sprite just above the head. Players with neither name nor number get
 * no label.
 *
 * @param {THREE.Object3D} container - player wrapper/group (label is parented to it)
 * @param {object} playerInfo - { name, number }
 * @param {number} [y=PLAYER_LABEL_Y.character] - height above the player's feet
 * @returns {THREE.Sprite|null} the label sprite, or null when there's no text
 */
export function attachPlayerLabel(container, playerInfo, y = PLAYER_LABEL_Y.character) {
    const text = playerInfo?.name || (playerInfo?.number != null && playerInfo?.number !== ''
        ? `#${playerInfo.number}` : '');
    if (!text) return null;
    const sprite = createPlayerLabel(text);
    sprite.position.set(0, y, 0);
    container.add(sprite);
    return sprite;
}

/**
 * Create a player 3D object with textures, hair, and pose animation.
 *
 * Three code paths (checked in order):
 *   1. Character asset system (_charCache.baseMesh) — SkeletonUtils.clone of
 *      the base mesh, per-player textures resolved from team/skin/hair styles,
 *      hair attached to Head bone, and distance-based pose animation applied.
 *   2. Legacy GLB cache (_playerModelCache) — simple clone with shadows only.
 *   3. Procedural fallback — builds a full player from primitive geometries
 *      (torso, arms, shorts, socks, legs, shoes with studs, head, neck, hair).
 *
 * Pose system details:
 *   pose_t (T-pose / binding pose) is applied first via AnimationMixer so the
 *   Head bone is in its correct world position.  Hair is then attached to the
 *   Head bone via headBone.attach() (which preserves world transform).  After
 *   attachment, pose_t is stopped and the distance-appropriate pose
 *   (stand/walk/jog/run) is played.
 *
 * @param {object} playerInfo - { name, number, skinStyle, hairStyle, position, ... }
 * @param {object} [options] - player appearance and context options
 * @param {string} [options.color1] - shirt colour (procedural fallback)
 * @param {string} [options.color2] - shorts/accent colour (procedural fallback)
 * @param {string} [options.color3] - socks colour (procedural fallback)
 * @param {string} [options.color4] - shoes colour (procedural fallback)
 * @param {string} [options.skinStyle='style_000'] - skin texture style key
 * @param {string} [options.hairStyle='style_000'] - hair mesh + texture style key
 * @param {boolean} [options.isGK=false] - if true, use GK team textures
 * @param {string} [options.side=''] - 'home' or 'away' (for texture resolution)
 * @param {object} [options.teamTextures=null] - custom team texture URLs
 * @param {number} [options.moveDistance=0] - distance to next phase (for pose)
 * @param {string} [options.numberColor='#ffffff'] - tint for number mesh
 * @returns {THREE.Group} player wrapper group with userData.playerInfo,
 *                        userData.mixer, userData.currentAction, userData.currentPose
 */
export function createPlayerObject(playerInfo, options = {}) {
    const {
        color1: shirtColor = '#ff0000', color2: shortsColor = '#000000',
        color3: socksColor = '#ff0000', color4: shoesColor = '#000000',
        skinStyle = 'style_000', hairStyle = 'style_000',
        isGK = false, side = '', teamTextures = null,
        moveDistance = 0,
        numberColor = '#ffffff',
    } = options;

    const group = new THREE.Group();
    group.name = getPlayerObjectName(playerInfo, '');
    group.userData.playerInfo = playerInfo;

    // ── Character model path: clone base mesh, apply textures + hair ──
    if (_charCache.baseMesh) {
        const wrapper = new THREE.Group();
        wrapper.name = getPlayerObjectName(playerInfo, '');
        wrapper.userData.playerInfo = playerInfo;

        // Use SkeletonUtils.clone for proper skinned mesh cloning
        // (regular clone(true) doesn't properly duplicate skeletons)
        const model = _skeletonClone(_charCache.baseMesh);

        // Resolve textures for this player
        const resolvedTextures = resolveTeamTextures(teamTextures, isGK, side);
        const skinTex = _charCache.skinTextures[skinStyle] || _charCache.skinTextures['style_000'];
        const bootsTex = _charCache.bootsTexture;

        // Assign textures directly — each mesh gets its respective texture
        const shirtFrontTex = resolvedTextures.shirtFront || null;
        const shirtBackTex = resolvedTextures.shirtBack || shirtFrontTex;
        const shortsFrontTex = resolvedTextures.shortsFront || null;
        const shortsBackTex = resolvedTextures.shortsBack || shortsFrontTex;
        const socksTex = resolvedTextures.socksFront || null;

        /**
         * Build materials for a mesh that may have multiple material slots (front/back).
         * Matches by original material name; falls back to materialIndex (0=front, 1=back).
         */
        function buildMultiMaterial(origMaterials, frontTex, backTex, roughness, metalness) {
            return origMaterials.map((origMat, idx) => {
                return new THREE.MeshStandardMaterial({
                    map: idx === 0 ? frontTex : (backTex || frontTex),
                    roughness, metalness,
                });
            });
        }

        // Traverse and assign materials based on node name
        model.traverse((c) => {
            if (c.isMesh) {
                c.castShadow = true;
                c.receiveShadow = true;
                c.userData.isPlayerBody = true;

                // Match by MATERIAL NAME (not node name) — GLB exports material names from Blender
                const mats = Array.isArray(c.material) ? c.material : [c.material];
                const matNames = mats.map(m => (m?.name || '').toLowerCase());
                const hasMatName = (keywords) => matNames.some(n => keywords.some(k => n.includes(k)));

                if (hasMatName(['skin'])) {
                    c.material = new THREE.MeshStandardMaterial({
                        map: skinTex || null,
                        roughness: 0.65, metalness: 0.0,
                    });
                } else if (hasMatName(['shirt'])) {
                    // Check if this mesh's material is specifically "back"
                    const isBack = matNames.some(n => n.includes('back'));
                    if (Array.isArray(c.material) && c.material.length >= 2) {
                        c.material = buildMultiMaterial(c.material, shirtFrontTex, shirtBackTex, 0.48, 0.08);
                    } else {
                        c.material = new THREE.MeshStandardMaterial({
                            map: isBack ? (shirtBackTex || shirtFrontTex || null) : (shirtFrontTex || null),
                            roughness: 0.48, metalness: 0.08,
                        });
                    }
                } else if (hasMatName(['shorts'])) {
                    const isBack = matNames.some(n => n.includes('back'));
                    if (Array.isArray(c.material) && c.material.length >= 2) {
                        c.material = buildMultiMaterial(c.material, shortsFrontTex, shortsBackTex, 0.48, 0.08);
                    } else {
                        c.material = new THREE.MeshStandardMaterial({
                            map: isBack ? (shortsBackTex || shortsFrontTex || null) : (shortsFrontTex || null),
                            roughness: 0.48, metalness: 0.08,
                        });
                    }
                } else if (hasMatName(['boots', 'shoe'])) {
                    c.material = new THREE.MeshStandardMaterial({
                        map: bootsTex || null,
                        roughness: 0.35, metalness: 0.15,
                    });
                } else if (hasMatName(['socks', 'sock'])) {
                    // Socks — load sock texture, no color override
                    c.material = new THREE.MeshStandardMaterial({
                        map: socksTex || null,
                        roughness: 0.55, metalness: 0.05,
                    });
                } else if ((c.name || '').toLowerCase() === 'number') {
                    // Number mesh — alpha cutout with team number colour tint
                    const num = String(playerInfo.number || '').trim().replace(/^0+/, '') || '0';
                    const padded = num.padStart(2, '0');
                    const numTex = _charCache.numberTextures[padded] || null;
                    c.material = new THREE.MeshStandardMaterial({
                        map: numTex,
                        color: numberColor,
                        transparent: true,
                        alphaTest: 0.5,
                        roughness: 0.6, metalness: 0.0,
                        side: THREE.DoubleSide,
                    });
                } else {
                    // Unknown mesh — leave as-is or apply skin as default
                    c.material = new THREE.MeshStandardMaterial({
                        map: skinTex || null,
                        roughness: 0.65, metalness: 0.0,
                    });
                }
            }
        });

        wrapper.add(model);

        // ── Animation: apply pose_t first to set correct bone positions for hair attachment ──
        const clips = _charCache.animations || [];
        if (clips.length > 0) {
            const mixer = new THREE.AnimationMixer(model);

            // Step 1: Apply pose_t (binding/T-pose) to set bones in correct position
            const tPoseClip = clips.find(c => c.name === 'pose_t');
            if (tPoseClip) {
                const tPoseAction = mixer.clipAction(tPoseClip);
                tPoseAction.play();
                tPoseAction.setEffectiveWeight(1);
                mixer.update(0);  // Force update so bone world matrices are current
            }

            // Step 2: Attach hair to Head bone while in T-pose (correct bone position)
            const hairSource = _charCache.hairMeshes[hairStyle] || _charCache.hairMeshes['style_000'];
            const resolvedHairStyle = _charCache.hairMeshes[hairStyle] ? hairStyle : 'style_000';
            const hairTex = _charCache.hairTextures[resolvedHairStyle];
            const hairNormal = _charCache.hairNormalTextures[resolvedHairStyle];
            if (hairSource) {
                const hair = hairSource.clone(true);
                hair.traverse((c) => {
                    if (c.isMesh) {
                        c.castShadow = true;
                        c.userData.isPlayerBody = true;
                        c.material = new THREE.MeshStandardMaterial({
                            map: hairTex || null,
                            normalMap: hairNormal || null,
                            alphaTest: hairNormal ? 0.08 : 0,
                            alphaToCoverage: Boolean(hairNormal),
                            normalScale: new THREE.Vector2(0.30, 0.30),
                            color: hairTex ? 0xffffff : 0x3a2a1a,
                            roughness: 0.72, metalness: 0.0,
                        });
                    }
                });

                // Add to wrapper (same world space as model)
                wrapper.add(hair);
                wrapper.updateMatrixWorld(true);

                // Find Head bone and reparent hair (preserves world transform)
                let headBone = null;
                model.traverse((c) => {
                    if (c.isBone && c.name === 'Head') headBone = c;
                });
                if (headBone) {
                    headBone.attach(hair);
                }
            }

            // Step 3: Stop T-pose and switch to movement pose
            if (tPoseClip) {
                const tPoseAction = mixer.clipAction(tPoseClip);
                tPoseAction.stop();
            }

            const poseName = 'pose_stand'; // layout phase — players rest until the timeline plays
            const clip = clips.find(c => c.name === poseName) || clips.find(c => c.name === 'pose_stand');
            if (clip) {
                const action = mixer.clipAction(clip);
                action.play();
                action.setEffectiveWeight(1);
                // Random per-player time offset so idle/loop animations aren't in sync
                if (clip.duration > 0) {
                    action.time = Math.random() * clip.duration;
                }
                wrapper.userData.currentAction = action;
                wrapper.userData.currentPose = poseName;
            }
            wrapper.userData.mixer = mixer;
            wrapper.userData.clips = clips;
        } else {
            // No animations — just attach hair directly
            const hairSource = _charCache.hairMeshes[hairStyle] || _charCache.hairMeshes['style_000'];
            const resolvedHairStyle = _charCache.hairMeshes[hairStyle] ? hairStyle : 'style_000';
            const hairTex = _charCache.hairTextures[resolvedHairStyle];
            const hairNormal = _charCache.hairNormalTextures[resolvedHairStyle];
            if (hairSource) {
                const hair = hairSource.clone(true);
                hair.traverse((c) => {
                    if (c.isMesh) {
                        c.castShadow = true;
                        c.userData.isPlayerBody = true;
                        c.material = new THREE.MeshStandardMaterial({
                            map: hairTex || null,
                            normalMap: hairNormal || null,
                            alphaTest: hairNormal ? 0.08 : 0,
                            alphaToCoverage: Boolean(hairNormal),
                            normalScale: new THREE.Vector2(0.30, 0.30),
                            color: hairTex ? 0xffffff : 0x3a2a1a,
                            roughness: 0.72, metalness: 0.0,
                        });
                    }
                });
                wrapper.add(hair);
                wrapper.updateMatrixWorld(true);
                let headBone = null;
                model.traverse((c) => {
                    if (c.isBone && c.name === 'Head') headBone = c;
                });
                if (headBone) {
                    headBone.attach(hair);
                }
            }
        }

        // ── Shadow blob ──
        wrapper.add(createShadowBlob(0.38));

        // ── Name label (camera-facing, toggled from the viewport nav) ──
        attachPlayerLabel(wrapper, playerInfo, PLAYER_LABEL_Y.character);

        return wrapper;
    }

    // ── Legacy GLB cache path (old player.glb) ──
    if (_playerModelCache) {
        const wrapper = new THREE.Group();
        wrapper.name = getPlayerObjectName(playerInfo, '');
        wrapper.userData.playerInfo = playerInfo;

        const model = _playerModelCache.clone(true);

        // Enable shadows only — do NOT touch materials, colors, or transparency
        model.traverse((c) => {
            if (c.isMesh) {
                c.castShadow = true;
                c.receiveShadow = true;
                c.userData.isPlayerBody = true;
            }
        });

        wrapper.add(model);

        // ── Shadow blob ──
        wrapper.add(createShadowBlob(0.38));

        // ── Name label (camera-facing, toggled from the viewport nav) ──
        attachPlayerLabel(wrapper, playerInfo, PLAYER_LABEL_Y.character);

        return wrapper;
    }

    // ── Procedural fallback (below) ──

    const jerseyColor = shirtColor;
    const accentColor = shortsColor;

    // ── Materials with subtle fabric sheen ──
    const jerseyMat = new THREE.MeshStandardMaterial({
        color: jerseyColor,
        roughness: 0.48,
        metalness: 0.08,
    });
    const accentMat = new THREE.MeshStandardMaterial({
        color: accentColor,
        roughness: 0.48,
        metalness: 0.08,
    });
    const skinMat = new THREE.MeshStandardMaterial({ color: 0xe8b896, roughness: 0.65, metalness: 0.0 });
    const shortsMat = new THREE.MeshStandardMaterial({ color: 0x1a1a2e, roughness: 0.6, metalness: 0.05 });
    const sockMat = new THREE.MeshStandardMaterial({ color: jerseyColor, roughness: 0.55, metalness: 0.05 });
    const shoeMat = new THREE.MeshStandardMaterial({ color: 0x0a0a0f, roughness: 0.35, metalness: 0.15 });
    const studMat = new THREE.MeshStandardMaterial({ color: 0x888888, roughness: 0.3, metalness: 0.6 });

    // ── Torso (tapered cylinder = jersey, wider shoulders) ──
    const torso = new THREE.Mesh(
        new THREE.CylinderGeometry(0.26, 0.21, 0.60, 20),
        jerseyMat
    );
    torso.userData.isPlayerBody = true;
    torso.position.set(0, 0.75, 0);
    torso.castShadow = true;
    group.add(torso);

    // ── Jersey number on back (texture plane) ──
    const numBackCanvas = createNumberCanvas(playerInfo.number, jerseyColor, null);
    const numBackTex = new THREE.CanvasTexture(numBackCanvas);
    const numBackMat = new THREE.MeshBasicMaterial({ map: numBackTex, transparent: true, depthWrite: false });
    const numBack = new THREE.Mesh(new THREE.PlaneGeometry(0.22, 0.22), numBackMat);
    numBack.position.set(0, 0.82, -0.22);
    numBack.userData.isPlayerBody = true;
    group.add(numBack);

    // ── Arms (upper + lower, natural outward angle) ──
    const armUpperGeo = new THREE.CylinderGeometry(0.058, 0.048, 0.30, 10);
    const armLowerGeo = new THREE.CylinderGeometry(0.048, 0.038, 0.28, 10);
    // Left arm
    const armUL = new THREE.Mesh(armUpperGeo, jerseyMat);
    armUL.position.set(-0.30, 0.86, 0);
    armUL.rotation.z = 0.18;
    armUL.castShadow = true; armUL.userData.isPlayerBody = true;
    group.add(armUL);
    const armLL = new THREE.Mesh(armLowerGeo, skinMat);
    armLL.position.set(-0.36, 0.60, 0);
    armLL.rotation.z = 0.18;
    armLL.castShadow = true; armLL.userData.isPlayerBody = true;
    group.add(armLL);
    // Right arm
    const armUR = new THREE.Mesh(armUpperGeo, jerseyMat);
    armUR.position.set(0.30, 0.86, 0);
    armUR.rotation.z = -0.18;
    armUR.castShadow = true; armUR.userData.isPlayerBody = true;
    group.add(armUR);
    const armLR = new THREE.Mesh(armLowerGeo, skinMat);
    armLR.position.set(0.36, 0.60, 0);
    armLR.rotation.z = -0.18;
    armLR.castShadow = true; armLR.userData.isPlayerBody = true;
    group.add(armLR);

    // ── Shoulder accent stripe ──
    const shoulder = new THREE.Mesh(
        new THREE.TorusGeometry(0.23, 0.035, 8, 20),
        accentMat
    );
    shoulder.rotation.x = Math.PI / 2;
    shoulder.position.set(0, 0.98, 0);
    shoulder.userData.isPlayerBody = true;
    group.add(shoulder);

    // ── Shorts ──
    const shorts = new THREE.Mesh(
        new THREE.CylinderGeometry(0.22, 0.18, 0.27, 20),
        shortsMat
    );
    shorts.userData.isPlayerBody = true;
    shorts.position.set(0, 0.32, 0);
    shorts.castShadow = true;
    group.add(shorts);

    // ── Socks (team-colored, below shorts) ──
    const sockGeo = new THREE.CylinderGeometry(0.085, 0.07, 0.22, 12);
    const sockL = new THREE.Mesh(sockGeo, sockMat);
    sockL.position.set(-0.09, 0.08, 0);
    sockL.castShadow = true; sockL.userData.isPlayerBody = true;
    group.add(sockL);
    const sockR = new THREE.Mesh(sockGeo, sockMat);
    sockR.position.set(0.09, 0.08, 0);
    sockR.castShadow = true; sockR.userData.isPlayerBody = true;
    group.add(sockR);

    // ── Legs (skin, between shorts and socks) ──
    const legGeo = new THREE.CylinderGeometry(0.072, 0.06, 0.16, 10);
    const legL = new THREE.Mesh(legGeo, skinMat);
    legL.position.set(-0.09, 0.18, 0);
    legL.castShadow = true;
    group.add(legL);
    const legR = new THREE.Mesh(legGeo, skinMat);
    legR.position.set(0.09, 0.18, 0);
    legR.castShadow = true;
    group.add(legR);

    // ── Shoes with studs ──
    const shoeGeo = new THREE.BoxGeometry(0.14, 0.06, 0.24);
    const shoeL = new THREE.Mesh(shoeGeo, shoeMat);
    shoeL.position.set(-0.09, -0.13, 0.05);
    shoeL.castShadow = true; shoeL.userData.isPlayerBody = true;
    group.add(shoeL);
    const shoeR = new THREE.Mesh(shoeGeo, shoeMat);
    shoeR.position.set(0.09, -0.13, 0.05);
    shoeR.castShadow = true; shoeR.userData.isPlayerBody = true;
    group.add(shoeR);

    // Studs (small metal dots under shoes)
    const studGeo = new THREE.CylinderGeometry(0.015, 0.015, 0.03, 6);
    for (const xOff of [-0.09, 0.09]) {
        for (const zOff of [-0.06, 0.06, 0.0]) {
            const stud = new THREE.Mesh(studGeo, studMat);
            stud.position.set(xOff, -0.17, zOff);
            group.add(stud);
        }
    }

    // ── Head ──
    const head = new THREE.Mesh(
        new THREE.SphereGeometry(0.15, 24, 24),
        skinMat
    );
    head.position.set(0, 1.12, 0);
    head.castShadow = true;
    group.add(head);

    // ── Neck ──
    const neck = new THREE.Mesh(
        new THREE.CylinderGeometry(0.06, 0.07, 0.08, 10),
        skinMat
    );
    neck.position.set(0, 1.02, 0);
    group.add(neck);

    // ── Hair (simple cap, varies slightly) ──
    const hairMat = new THREE.MeshStandardMaterial({ color: 0x2a1810, roughness: 0.85 });
    const procHairStyle = (playerInfo.number || 0) % 3;
    if (procHairStyle === 0) {
        // Short cap
        const hair = new THREE.Mesh(
            new THREE.SphereGeometry(0.155, 16, 16, 0, Math.PI * 2, 0, Math.PI * 0.5),
            hairMat
        );
        hair.position.set(0, 1.14, 0);
        group.add(hair);
    } else if (procHairStyle === 1) {
        // Slightly longer (covers ears)
        const hair = new THREE.Mesh(
            new THREE.SphereGeometry(0.158, 16, 16, 0, Math.PI * 2, 0, Math.PI * 0.6),
            hairMat
        );
        hair.position.set(0, 1.13, 0);
        group.add(hair);
    } else {
        // Buzz cut (very short)
        const hair = new THREE.Mesh(
            new THREE.SphereGeometry(0.152, 16, 16, 0, Math.PI * 2, 0, Math.PI * 0.35),
            hairMat
        );
        hair.position.set(0, 1.15, 0);
        group.add(hair);
    }

    // ── Shadow blob ──
    group.add(createShadowBlob(0.38));

    // ── Name label (camera-facing, toggled from the viewport nav) ──
    attachPlayerLabel(group, playerInfo, PLAYER_LABEL_Y.procedural);

    return group;
}


// ═══════════════════════════════════════════════════════════════
//  BALL
//
//  ASSET: Ball Model — swap by placing ball.glb in src/assets3d/models/ball/
//  See src/assets3d/README.md for instructions
// ═══════════════════════════════════════════════════════════════

// Module-level cache: pre-load GLB once, clone synchronously on each use.
// This is critical — async ball creation breaks animation curve registration.
let _ballModelCache = null;

/**
 * Preload the GLB ball model (cached).
 * Auto-scales so the largest dimension = 0.56m (diameter for a 0.28m radius ball).
 * Must be called before createBallObject() so creation is synchronous.
 * @returns {Promise<THREE.Object3D|null>} the cached ball model, or null
 */
export async function preloadBallModel() {
    if (_ballModelCache) return _ballModelCache;
    if (!isAssetEnabled('ball')) return null;
    try {
        const model = await loadModel('ball');
        if (model) {
            // Auto-fit: scale so largest dimension = 0.56 (diameter for 0.28 radius)
            const box = new THREE.Box3().setFromObject(model);
            const size = new THREE.Vector3();
            box.getSize(size);
            const maxDim = Math.max(size.x, size.y, size.z);
            if (maxDim > 0) {
                const fitScale = 0.56 / maxDim;
                model.scale.multiplyScalar(fitScale);
            }
            _ballModelCache = model;
        }
    } catch (e) {
        console.error('[ball] preload failed, using procedural', e);
    }
    return _ballModelCache;
}

/**
 * Create a soccer ball 3D object.
 * Uses the cached GLB model if available (cloned synchronously), otherwise
 * falls back to a procedural sphere with a generated soccer-ball texture.
 * @param {number} [color=0xffffff] - ball colour (procedural fallback only)
 * @returns {THREE.Group} ball wrapper with userData.isBall = true
 */
export function createBallObject(color = 0xffffff) {
    let ballRadius = 0.28; // default radius (matches procedural fallback)

    if (_ballModelCache) {
        const wrapper = new THREE.Group();
        wrapper.name = 'BallObject';
        wrapper.userData.isBall = true;

        const model = _ballModelCache.clone(true);
        // Offset model so bottom sits at y=0 (ball center at radius height)
        const box = new THREE.Box3().setFromObject(model);
        model.position.y -= box.min.y;
        wrapper.add(model);

        // Compute actual radius from AABB half-height
        const modelBox = new THREE.Box3().setFromObject(wrapper);
        const modelSize = new THREE.Vector3();
        modelBox.getSize(modelSize);
        ballRadius = Math.max(modelSize.y, modelSize.x, modelSize.z) / 2;
        wrapper.userData.ballRadius = ballRadius;

        // Propagate userData for raycasting + shadows
        // (isBall only on wrapper — findUserDataRoot walks up to find it)
        wrapper.traverse((c) => {
            if (c.isMesh) {
                c.castShadow = true;
            }
        });

        // Shadow blob
        wrapper.add(createShadowBlob(0.32));

        return wrapper;
    }

    // Procedural fallback
    const group = new THREE.Group();
    group.name = 'BallObject';
    group.userData.isBall = true;
    group.userData.ballRadius = ballRadius;

    const ballTex = createSoccerBallTexture();
    const ballMat = new THREE.MeshStandardMaterial({
        map: ballTex,
        roughness: 0.28,
        metalness: 0.08,
    });

    const ball = new THREE.Mesh(
        new THREE.SphereGeometry(ballRadius, 48, 48),
        ballMat
    );
    ball.position.y = ballRadius;
    ball.castShadow = true;
    group.add(ball);

    // Shadow
    group.add(createShadowBlob(0.32));

    return group;
}


// ═══════════════════════════════════════════════════════════════
//  CONE
// ═══════════════════════════════════════════════════════════════

/**
 * Create a training cone 3D object.
 * @param {number|string} [color=0xffcc00] - cone colour (hex number or CSS string)
 * @returns {THREE.Group} cone group with userData.isCone = true
 */
export function createConeObject(color = 0xffcc00) {
    const group = new THREE.Group();
    group.name = 'ConeObject';
    group.userData.isCone = true;

    const coneColor = typeof color === 'string' ? color : '#' + color.toString(16).padStart(6, '0');
    const coneMat = new THREE.MeshStandardMaterial({
        color: coneColor,
        roughness: 0.35,
        metalness: 0.12,
        emissive: coneColor,
        emissiveIntensity: 0.12,
    });

    const cone = new THREE.Mesh(
        new THREE.ConeGeometry(0.20, 0.50, 24),
        coneMat
    );
    cone.position.set(0, 0.25, 0);
    cone.castShadow = true;
    group.add(cone);

    // Shadow
    group.add(createShadowBlob(0.26));

    return group;
}


// ═══════════════════════════════════════════════════════════════
//  TARGET
// ═══════════════════════════════════════════════════════════════

/**
 * Create a target 3D object (concentric rings + bullseye + pole + flag).
 * @param {number|string} [color=0xff0000] - ring colour (hex number or CSS string)
 * @returns {THREE.Group} target group with userData.isTarget = true
 */
export function createTargetObject(color = 0xff0000) {
    const group = new THREE.Group();
    group.name = 'TargetObject';
    group.userData.isTarget = true;

    const ringColors = [color, 0xffffff, color];
    for (let i = 0; i < 3; i++) {
        const radius = 0.55 - i * 0.16;
        const ringMat = new THREE.MeshStandardMaterial({
            color: ringColors[i],
            side: THREE.DoubleSide,
            transparent: true,
            opacity: 0.78,
            roughness: 0.35,
            emissive: ringColors[i],
            emissiveIntensity: 0.18,
        });
        const ring = new THREE.Mesh(
            new THREE.RingGeometry(radius - 0.06, radius, 32),
            ringMat
        );
        ring.rotation.x = -Math.PI / 2;
        ring.position.y = 0.02 + i * 0.002;
        group.add(ring);
    }

    // Center bullseye
    const dot = new THREE.Mesh(
        new THREE.CircleGeometry(0.12, 24),
        new THREE.MeshStandardMaterial({
            color: 0xff0000,
            side: THREE.DoubleSide,
            emissive: 0xff0000,
            emissiveIntensity: 0.35,
        })
    );
    dot.rotation.x = -Math.PI / 2;
    dot.position.y = 0.03;
    group.add(dot);

    // Vertical pole for visibility
    const poleMat = new THREE.MeshStandardMaterial({ color: 0xff0000, emissive: 0xff0000, emissiveIntensity: 0.15, roughness: 0.4 });
    const pole = new THREE.Mesh(
        new THREE.CylinderGeometry(0.025, 0.025, 0.6, 8),
        poleMat
    );
    pole.position.y = 0.3;
    pole.castShadow = true;
    group.add(pole);

    // Flag at top
    const flagMat = new THREE.MeshStandardMaterial({ color: 0xff0000, side: THREE.DoubleSide, emissive: 0xff0000, emissiveIntensity: 0.2 });
    const flag = new THREE.Mesh(new THREE.PlaneGeometry(0.2, 0.12), flagMat);
    flag.position.set(0.1, 0.55, 0);
    group.add(flag);

    return group;
}


// ═══════════════════════════════════════════════════════════════
//  TRANSITION ARROWS (straight, dashed, and curved with full easing control)
//
//  ASSET: Arrowhead Model — swap by placing arrowhead.glb in src/assets3d/models/arrowhead/
//  See src/assets3d/README.md for instructions
// ═══════════════════════════════════════════════════════════════

export function getPlayerObjectName(playerInfo, side = '') {
    return `${side}_${playerInfo.name}_${playerInfo.number}`;
}


// ═══════════════════════════════════════════════════════════════
//  ZONE (closed polygon with yellow shaded surface)
// ═══════════════════════════════════════════════════════════════

/**
 * Create a zone 3D object — a polygon or circle with optional filled surface,
 * outline, and vertex dots.  Used for highlighting tactical areas.
 *
 * The returned GROUP is positioned at the zone's CENTRE (opts.center) and the
 * shape is drawn in the group's local space, so moving the group moves the
 * whole zone (used by movement tracks and zone-level attachment).
 *
 * @param {string} [color='#ffeb3b'] - zone fill/outline colour (CSS string)
 * @param {number[][]} [points=[]] - array of [x, y, z] WORLD vertex positions
 * @param {number} [opacity=0.35] - fill surface opacity (0–1)
 * @param {boolean} [closed=true] - when true, zone is filled; when false, only lines
 * @param {number} [thickness=2] - outline line thickness (linewidth)
 * @param {Object}  [opts={}] - { center: [x,y,z], mode: 'static'|'circle'|'dynamic', radius }
 * @returns {THREE.Group} zone group with userData.isZone = true
 */
export function createZoneObject(color = '#ffeb3b', points = [], opacity = 0.35, closed = true, thickness = 2, opts = {}) {
    const group = new THREE.Group();
    group.name = 'ZoneObject';
    group.userData.isZone = true;

    // Opacity 0 = "this zone is not shown in this window". The fill alone can't
    // express that (the outline is an opaque material), so hide the whole group
    // — fill, outline and centre cross — instead of drawing an invisible fill
    // with a visible ring.
    const effOpacity = (typeof opacity === 'number' && isFinite(opacity)) ? opacity : 0.35;
    group.visible = effOpacity > 0;

    const center = opts.center || [0, 0, 0];
    const cx = center[0] || 0, cz = center[2] || 0;
    const mode = opts.mode || 'static';
    const radius = opts.radius ?? 3;

    // Local-space points (shape drawn around the group origin = the centre)
    const local = (p) => [p[0] - cx, p[1] ?? 0, p[2] - cz];

    const zoneColor = new THREE.Color(color);

    if (mode === 'circle') {
        // ── Circle zone: filled disc + outline ring around the centre ──
        if (closed) {
            const shape = new THREE.Shape();
            shape.absarc(0, 0, radius, 0, Math.PI * 2);
            const fillGeo = new THREE.ShapeGeometry(shape);
            const fillMat = new THREE.MeshBasicMaterial({
                color: zoneColor, transparent: true, opacity,
                side: THREE.DoubleSide, depthWrite: true,
            });
            const fillMesh = new THREE.Mesh(fillGeo, fillMat);
            fillMesh.rotation.x = Math.PI / 2;
            fillMesh.position.y = 0.15;
            fillMesh.userData.isZoneBody = true;
            group.add(fillMesh);
        }
        // Outline ring (thick cylinders around the circumference)
        const lineThick = Math.max(0.02, (thickness || 2) * 0.04);
        const lineMat = new THREE.MeshBasicMaterial({ color: zoneColor });
        const segments = 48;
        const ringPts = [];
        for (let i = 0; i <= segments; i++) {
            const a = (i / segments) * Math.PI * 2;
            ringPts.push([Math.cos(a) * radius, 0.16, Math.sin(a) * radius]);
        }
        for (let i = 0; i < ringPts.length - 1; i++) {
            const a = ringPts[i], b = ringPts[i + 1];
            const va = new THREE.Vector3(a[0], a[1], a[2]);
            const vb = new THREE.Vector3(b[0], b[1], b[2]);
            const dir = new THREE.Vector3().subVectors(vb, va);
            const len = dir.length();
            if (len < 0.001) continue;
            const cylGeo = new THREE.CylinderGeometry(lineThick, lineThick, len, 8);
            const cyl = new THREE.Mesh(cylGeo, lineMat);
            cyl.position.copy(va).add(vb).multiplyScalar(0.5);
            cyl.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir.clone().normalize());
            cyl.userData.isZoneBody = true;
            group.add(cyl);
        }
        // Centre cross — visual only (NOT an editable vertex; circle zones are
        // sized by radius, so a point handle at the centre would hijack clicks).
        buildCenterCross(group, zoneColor);

        group.position.set(cx, 0, cz);
        return group;
    }

    if (!points || points.length < 2) {
        group.position.set(cx, 0, cz);
        return group;
    }

    const localPoints = points.map(local);

    // ── Filled surface (only when closed) ──
    if (closed && localPoints.length >= 3) {
        const shape = new THREE.Shape();
        shape.moveTo(localPoints[0][0], localPoints[0][2]);
        for (let i = 1; i < localPoints.length; i++) {
            shape.lineTo(localPoints[i][0], localPoints[i][2]);
        }
        shape.closePath();

        const fillGeo = new THREE.ShapeGeometry(shape);
        const fillMat = new THREE.MeshBasicMaterial({
            color: zoneColor,
            transparent: true,
            opacity: opacity,
            side: THREE.DoubleSide,
            depthWrite: true,
        });
        const fillMesh = new THREE.Mesh(fillGeo, fillMat);
        fillMesh.rotation.x = Math.PI / 2;
        fillMesh.position.y = 0.15;
        fillMesh.userData.isZoneBody = true;
        group.add(fillMesh);
    }

    // ── Outline (thick line using cylinders between consecutive points) ──
    const lineThick = Math.max(0.02, (thickness || 2) * 0.04); // convert 1-10 to 0.04-0.40 world units
    const lineMat = new THREE.MeshBasicMaterial({ color: zoneColor });
    const buildThickSegment = (p1, p2) => {
        const a = new THREE.Vector3(p1[0], 0.16, p1[2]);
        const b = new THREE.Vector3(p2[0], 0.16, p2[2]);
        const dir = new THREE.Vector3().subVectors(b, a);
        const len = dir.length();
        if (len < 0.001) return;
        const cylGeo = new THREE.CylinderGeometry(lineThick, lineThick, len, 8);
        const cyl = new THREE.Mesh(cylGeo, lineMat);
        cyl.position.copy(a).add(b).multiplyScalar(0.5);
        cyl.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir.clone().normalize());
        cyl.userData.isZoneBody = true;
        group.add(cyl);
    };
    for (let i = 0; i < localPoints.length - 1; i++) {
        buildThickSegment(localPoints[i], localPoints[i + 1]);
    }
    if (closed && localPoints.length >= 3) {
        buildThickSegment(localPoints[localPoints.length - 1], localPoints[0]);
    }

    // ── Vertex dots ──
    localPoints.forEach((p, i) => {
        const dot = new THREE.Mesh(
            new THREE.SphereGeometry(0.18, 16, 16),
            new THREE.MeshBasicMaterial({
                color: zoneColor,
            })
        );
        dot.position.set(p[0], 0.2, p[2]);
        dot.userData.isZoneBody = true;
        dot.userData.isZonePoint = true;
        dot.userData.pointIndex = i;
        dot.castShadow = false;
        group.add(dot);
    });

    // Centre cross — marks the zone's position (its group origin). The shape's
    // vertices are positioned relative to this point, and the selection ring
    // centres on it too.
    buildCenterCross(group, zoneColor);

    group.position.set(cx, 0, cz);
    return group;
}

/**
 * Build the small centre-cross indicator for a zone (a + on the ground at the
 * zone's position / group origin). All pieces flagged isZoneBody so they are
 * treated as part of the zone shape for raycasting/cleanup.
 */
function buildCenterCross(group, zoneColor) {
    const armLen = 0.45;   // half-length of each arm (world units)
    const gap = 0.12;      // inner gap around the centre
    const h = 0.05;        // height above the pitch
    const mat = new THREE.MeshBasicMaterial({ color: zoneColor });
    const buildBar = (dx, dz) => {
        const bar = new THREE.Mesh(
            new THREE.BoxGeometry(dx === 0 ? 0.06 : armLen * 2, h, dz === 0 ? 0.06 : armLen * 2),
            mat
        );
        bar.position.set(dx * (gap + armLen), h / 2, dz * (gap + armLen));
        bar.userData.isZoneBody = true;
        bar.userData.isZoneCenterCross = true;
        bar.visible = false; // centre cross shows ONLY while the zone is selected
        group.add(bar);
    };
    buildBar(1, 0);   // +X arm
    buildBar(-1, 0);  // −X arm
    buildBar(0, 1);   // +Z arm
    buildBar(0, -1);  // −Z arm
}


/**
 * Create zone edit-mode point markers (like manual path points).
 * Each vertex is a white sphere with an emissive glow and a numbered sprite.
 * Used when the user is editing zone vertices (add/move/remove).
 * @param {number[][]} points - array of [x, y, z] vertex positions
 * @param {string} [color='#ffeb3b'] - emissive glow colour (CSS string)
 * @returns {THREE.Group} editor group with userData.isZoneEditor = true
 */
export function createZoneEditPoints(points, color = '#ffeb3b', pointRefs = []) {
    const group = new THREE.Group();
    group.userData.isZoneEditor = true;

    const editColor = new THREE.Color(color);
    const attachedColor = new THREE.Color('#ef4444'); // red for attached points

    points.forEach((p, i) => {
        const isAttached = pointRefs[i]?.ref;
        const dot = new THREE.Mesh(
            new THREE.SphereGeometry(0.45, 20, 20),
            new THREE.MeshStandardMaterial({
                color: 0xffffff,
                roughness: 0.2,
                metalness: 0.2,
                emissive: isAttached ? attachedColor : editColor,
                emissiveIntensity: isAttached ? 0.7 : 0.4,
            })
        );
        dot.position.set(p[0], 0.15, p[2]);
        dot.userData.isZonePoint = true;
        dot.userData.pointIndex = i;
        dot.castShadow = true;
        group.add(dot);

        // Number label
        const canvas = document.createElement('canvas');
        canvas.width = 64; canvas.height = 64;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffeb3b';
        ctx.beginPath(); ctx.arc(32, 34, 28, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = '#000';
        ctx.font = 'bold 28px Arial';
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText(String(i + 1), 32, 34);
        const tex = new THREE.CanvasTexture(canvas);
        const sprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, transparent: true, depthTest: false }));
        sprite.scale.set(0.6, 0.6, 0.6);
        sprite.position.set(p[0], 0.75, p[2]);
        group.add(sprite);
    });

    return group;
}



